import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { clinicUsersService } from '@/services/clinic_users_service'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'

export const formSchema = z.object({
  account: z.string().min(1, { message: '請輸入投資者帳號' }),
})

interface AddInvestorDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  clinicId: number
  onSuccess: () => void
}

export function AddInvestorDialog({
  open,
  onOpenChange,
  clinicId,
  onSuccess,
}: AddInvestorDialogProps) {
  const { token } = useAuth()

  const form = useForm<z.infer<typeof formSchema>>({
    defaultValues: {
      account: '',
    },
  })

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    if (!token) return

    const result = await clinicUsersService.addUserToClinicByAccount(
      {
        clinic_id: clinicId,
        account: values.account,
      },
      token,
    )

    if (result.success) {
      toast.success('投資者加入診所成功')
      form.reset()
      onOpenChange(false)
      onSuccess()
    } else {
      if (result.error?.code === 'USER_NOT_FOUND') {
        toast.error('找不到該帳號的投資者')
      } else if (result.error?.code === 'CLINIC_USER_ALREADY_EXISTS') {
        toast.error('該投資者已經關聯此診所')
      } else {
        toast.error(result.error?.message || '加入投資者失敗')
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>加入投資者</DialogTitle>
          <DialogDescription>將現有的投資者帳號關聯到此診所</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="account"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>投資者帳號</FormLabel>
                  <FormControl>
                    <Input placeholder="請輸入投資者帳號" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="submit">加入</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
